"use client"

import * as React from "react"

/**
 * Kiln Wordmark Footer — by Kedhareswer Naidu, via 21st.dev
 * https://21st.dev/@kedhareswer/components/kiln-wordmark-footer
 *
 * A two-line motto, a back-to-top key, three columns (address, navigation,
 * follow), a giant lowercase wordmark and a pill-outlined legal bar, over a
 * "tile": a field, a big disc and a slab. The disc leans toward the pointer,
 * and a click on any empty part of the panel turns the tile to its next
 * composition.
 *
 * The wordmark is not a font. It is drawn from a small procedural geometric
 * alphabet (a–z), so its stroke weight is a number: letters swell under the
 * pointer like a variable font, press flat when clicked, and rise out of the
 * baseline the first time the footer is seen.
 *
 * Adapté pour le site : contenu en props sans valeurs par défaut, liens externes
 * dans un nouvel onglet, libellés en français.
 */

// #region kiln
// Pure: glyph geometry, layout, weight lens and tile maths.

type Pt = [number, number]

/** x-height, ascender and descender of the alphabet, in glyph units. */
const XH = 100
const ASC = 36
const DESC = 36
/** Superellipse exponent of every bowl: 2 is an ellipse, higher is squarer. */
const SQUARE = 2.4
const TAU = Math.PI * 2

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v)

/** Shoelace area. Positive is clockwise on screen (y points down). */
const signedArea = (p: Pt[]): number => {
  let a = 0
  for (let i = 0; i < p.length; i++) {
    const j = (i + 1) % p.length
    a += p[i][0] * p[j][1] - p[j][0] * p[i][1]
  }
  return a / 2
}

/** Fill shapes run clockwise, holes anticlockwise, so one nonzero path can hold a whole glyph. */
const orient = (p: Pt[], sign: 1 | -1): Pt[] => (signedArea(p) * sign >= 0 ? p : p.slice().reverse())

const sePt = (cx: number, cy: number, rx: number, ry: number, t: number): Pt => {
  const c = Math.cos(t)
  const s = Math.sin(t)
  return [cx + rx * Math.sign(c) * Math.pow(Math.abs(c), 2 / SQUARE), cy + ry * Math.sign(s) * Math.pow(Math.abs(s), 2 / SQUARE)]
}

/**
 * A superellipse bowl inside the box (x0, y0)–(x1, y1), V thick at the sides
 * and h thick at top and bottom: an outline (clockwise) and its counter
 * (anticlockwise). Open letters are this ring cut down with `keep`.
 */
const ring = (x0: number, y0: number, x1: number, y1: number, V: number, h: number): Pt[][] => {
  const cx = (x0 + x1) / 2
  const cy = (y0 + y1) / 2
  const rx = (x1 - x0) / 2
  const ry = (y1 - y0) / 2
  const irx = Math.max(1, rx - V)
  const iry = Math.max(1, ry - h)
  const outer: Pt[] = []
  const inner: Pt[] = []
  for (let i = 0; i < 96; i++) {
    const t = (i / 96) * TAU
    outer.push(sePt(cx, cy, rx, ry, t))
    inner.push(sePt(cx, cy, irx, iry, t))
  }
  return [orient(outer, 1), orient(inner, -1)]
}

/** A half-plane: keep points where (p[axis] - at) * side <= 0. side 1 keeps below/left, -1 above/right. */
type Half = [axis: 0 | 1, at: number, side: 1 | -1]

/** Sutherland–Hodgman against one half-plane. Orientation is preserved. */
const clipHalf = (poly: Pt[], [axis, at, side]: Half): Pt[] => {
  const out: Pt[] = []
  const inside = (p: Pt) => (p[axis] - at) * side <= 0
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i]
    const b = poly[(i + 1) % poly.length]
    const ia = inside(a)
    const ib = inside(b)
    if (ia) out.push(a)
    if (ia !== ib) {
      const u = (at - a[axis]) / (b[axis] - a[axis])
      out.push([a[0] + (b[0] - a[0]) * u, a[1] + (b[1] - a[1]) * u])
    }
  }
  return out
}

/** The part of a shape (outlines and counters) inside every half-plane: flat, cut terminals. */
const keep = (shape: Pt[][], ...halves: Half[]): Pt[][] =>
  shape.map((poly) => halves.reduce(clipHalf, poly)).filter((poly) => poly.length >= 3 && Math.abs(signedArea(poly)) > 1e-6)

const rect = (x0: number, y0: number, x1: number, y1: number): Pt[] =>
  orient([[x0, y0], [x1, y0], [x1, y1], [x0, y1]], 1)

/** A slanted stroke from (xa, ya) to (xb, yb), `t` wide measured horizontally. */
const diag = (xa: number, ya: number, xb: number, yb: number, t: number): Pt[] =>
  orient([[xa, ya], [xa + t, ya], [xb + t, yb], [xb, yb]], 1)

type GlyphFn = (V: number, h: number) => Pt[][]

const B = 112 // the bowl width shared by a b d e g o p q u n h

/**
 * The alphabet: [advance width, shapes]. Advance widths never depend on the
 * weight, so a letter can swell without the word re-flowing. Strokes that
 * sit on an edge grow inward; free-standing stems grow from their centre.
 */
const GLYPHS: Record<string, [number, GlyphFn]> = {
  a: [B, (V, h) => [...ring(0, 0, B, XH, V, h), rect(B - V, 0, B, XH)]],
  b: [B, (V, h) => [rect(0, -ASC, V, XH), ...ring(0, 0, B, XH, V, h)]],
  c: [82, (V, h) => keep(ring(0, 0, 122, XH, V, h), [0, 82, 1])],
  d: [B, (V, h) => [...ring(0, 0, B, XH, V, h), rect(B - V, -ASC, B, XH)]],
  e: [B, (V, h) => {
    const o = ring(0, 0, B, XH, V, h)
    return [...keep(o, [1, XH / 2, 1]), ...keep(o, [1, XH / 2, -1], [0, 82, 1]), rect(V * 0.5, XH / 2 - h / 2, B, XH / 2 + h / 2)]
  }],
  f: [78, (V, h) => [rect(10, 26, 10 + V, XH), ...keep(ring(10, -ASC, 110, 88, V, h), [1, 26, 1], [0, 78, 1]), rect(0, 0, 74, h)]],
  g: [B, (V, h) => [...ring(0, 0, B, XH, V, h), rect(B - V, 0, B, 92), ...keep(ring(0, 48, B, XH + DESC, V, h), [1, 92, -1], [0, 14, -1])]],
  h: [B, (V, h) => [rect(0, -ASC, V, XH), ...keep(ring(0, 0, B, XH, V, h), [1, XH / 2, 1]), rect(B - V, XH / 2, B, XH)]],
  i: [30, (V) => [rect(15 - V / 2, 0, 15 + V / 2, XH)]],
  j: [46, (V, h) => [rect(31 - V / 2, 0, 31 + V / 2, XH + DESC), rect(0, XH + DESC - h, 31, XH + DESC)]],
  k: [100, (V) => [rect(0, -ASC, V, XH), diag(V * 0.7, 66, 100 - V * 1.1, 0, V * 1.1), diag(36, 44, 100 - V, XH, V)]],
  l: [30, (V) => [rect(15 - V / 2, -ASC, 15 + V / 2, XH)]],
  m: [184, (V, h) => [
    rect(0, 0, V, XH),
    ...keep(ring(0, 0, 92 + V / 2, XH, V, h), [1, XH / 2, 1]),
    ...keep(ring(92 - V / 2, 0, 184, XH, V, h), [1, XH / 2, 1]),
    rect(92 - V / 2, XH / 2, 92 + V / 2, XH),
    rect(184 - V, XH / 2, 184, XH),
  ]],
  n: [B, (V, h) => [rect(0, 0, V, XH), ...keep(ring(0, 0, B, XH, V, h), [1, XH / 2, 1]), rect(B - V, XH / 2, B, XH)]],
  o: [B, (V, h) => ring(0, 0, B, XH, V, h)],
  p: [B, (V, h) => [rect(0, 0, V, XH + DESC), ...ring(0, 0, B, XH, V, h)]],
  q: [B, (V, h) => [...ring(0, 0, B, XH, V, h), rect(B - V, 0, B, XH + DESC)]],
  r: [84, (V, h) => [rect(0, 0, V, XH), ...keep(ring(0, 0, 108, XH + 10, V, h), [1, (XH + 10) / 2, 1], [0, 84, 1])]],
  s: [104, (V, h) => {
    const m = XH / 2
    const up = ring(0, 0, 104, m + h / 2, V, h)
    const lo = ring(0, m - h / 2, 104, XH, V, h)
    return [
      ...keep(up, [0, 52, 1]),
      ...keep(up, [0, 52, -1], [0, 90, 1], [1, (m + h / 2) / 2, 1]),
      ...keep(lo, [0, 52, -1]),
      ...keep(lo, [0, 52, 1], [0, 14, -1], [1, (m - h / 2 + XH) / 2, -1]),
    ]
  }],
  t: [80, (V, h) => [rect(12, -ASC * 0.8, 12 + V, 56), rect(0, 0, 76, h), ...keep(ring(12, 12, 112, XH, V, h), [1, 56, -1], [0, 80, 1])]],
  u: [B, (V, h) => [...keep(ring(0, 0, B, XH, V, h), [1, XH / 2, -1]), rect(0, 0, V, XH / 2), rect(B - V, 0, B, XH)]],
  v: [106, (V) => [diag(0, 0, 53 - V / 2, XH, V), diag(106 - V, 0, 53 - V / 2, XH, V)]],
  w: [176, (V) => [
    diag(0, 0, 44 - V / 2, XH, V),
    diag(88 - V / 2, 0, 44 - V / 2, XH, V),
    diag(88 - V / 2, 0, 132 - V / 2, XH, V),
    diag(176 - V, 0, 132 - V / 2, XH, V),
  ]],
  x: [106, (V) => [diag(0, 0, 106 - V, XH, V), diag(106 - V, 0, 0, XH, V)]],
  y: [106, (V) => {
    const foot = 53 - V / 2
    const slope = (foot - (106 - V)) / XH
    return [diag(0, 0, foot, XH, V), diag(106 - V, 0, 106 - V + slope * (XH + DESC), XH + DESC, V)]
  }],
  z: [98, (V, h) => [rect(0, 0, 98, h), rect(0, XH - h, 98, XH), diag(98 - V * 1.15, h * 0.5, 0, XH - h * 0.5, V * 1.15)]],
  "-": [60, (_V, h) => [rect(0, XH / 2 - h / 2, 60, XH / 2 + h / 2)]],
  ".": [30, (V) => [rect(15 - V / 2, XH - V, 15 + V / 2, XH)]],
  " ": [46, () => []],
}

/** Stroke weights for a weight factor k: k = 1 is the reference, verticals move more than horizontals. */
const strokes = (k: number): { V: number; h: number } => {
  const w = clamp(Number.isFinite(k) ? k : 1, 0.4, 1.6)
  return { V: 30 * w, h: 19 * (0.55 + 0.45 * w) }
}

const n1 = (v: number): string => String(Math.round(v * 10) / 10)

/** One glyph as an SVG path at weight k, shifted right by dx. Unknown characters are "". */
const glyphPath = (ch: string, k: number, dx: number = 0): string => {
  const g = GLYPHS[ch]
  if (!g) return ""
  const { V, h } = strokes(k)
  return g[1](V, h)
    .map((poly) => "M" + poly.map((p) => n1(p[0] + dx) + " " + n1(p[1])).join("L") + "Z")
    .join("")
}

/** True when every character of `word` can be drawn by the alphabet. */
const supported = (word: string): boolean => word.length > 0 && [...word].every((c) => c in GLYPHS)

type Placed = { ch: string; x: number; w: number }

/**
 * Lay a word out on one line: where each letter starts, the total width, and
 * the vertical extent (letters with ascenders/descenders open the box up).
 */
const layoutWord = (word: string, tracking: number = 10): { letters: Placed[]; width: number; top: number; bottom: number } => {
  const letters: Placed[] = []
  let x = 0
  for (const ch of word) {
    const g = GLYPHS[ch]
    if (!g) continue
    letters.push({ ch, x, w: g[0] })
    x += g[0] + tracking
  }
  const width = letters.length ? x - tracking : 0
  const top = /[bdfhklt]/.test(word) ? -ASC : 0
  const bottom = /[gjpqy]/.test(word) ? XH + DESC : XH
  return { letters, width, top, bottom }
}

/** The weight lens: each letter's target weight for a pointer at `px` (null = away). */
const lensWeights = (centres: number[], px: number | null, base: number, boost: number, radius: number): number[] =>
  centres.map((c) => {
    if (px === null || !Number.isFinite(px) || !(radius > 0)) return base
    const d = (c - px) / radius
    return base + boost * Math.exp(-d * d)
  })

/** Frame-rate independent ease toward a target: `k` is the fraction closed per 1/60 s. */
const approach = (from: number, to: number, k: number, dt: number): number =>
  to + (from - to) * Math.pow(1 - clamp(k, 0, 1), clamp(dt, 0, 0.1) * 60)

/**
 * The tile's compositions, in fractions of the panel: a disc (cx, cy, r of the
 * width) and a slab (x, width). Click the panel to turn to the next one.
 */
type Tile = { cx: number; cy: number; r: number; sx: number; sw: number }
const TILES: Tile[] = [
  { cx: 0.385, cy: 0.5, r: 0.415, sx: 0.755, sw: 0.245 },
  { cx: 0.615, cy: 0.5, r: 0.415, sx: 0, sw: 0.245 },
  { cx: 0.5, cy: 1.04, r: 0.5, sx: 0.5, sw: 0 },
  { cx: 1, cy: 0, r: 0.6, sx: 0, sw: 0.3 },
]

/** Index of the tile after `i`, wrapping. */
const nextTile = (i: number, n: number = TILES.length): number => (((Math.trunc(i) + 1) % n) + n) % n

/** The tile with the disc nudged toward the pointer (px, py in -1 → 1). */
/** The tile shrunk by `k` (disc radius and slab width); a slab on the right edge stays on it. */
const scaleTile = (t: Tile, k: number): Tile => {
  const sw = t.sw * k
  return { ...t, r: t.r * k, sw, sx: t.sx + t.sw >= 0.999 ? 1 - sw : t.sx }
}

const leanTile = (t: Tile, px: number, py: number, amount: number = 0.035): Tile => ({
  ...t,
  cx: t.cx + amount * clamp(Number.isFinite(px) ? px : 0, -1, 1),
  cy: t.cy + amount * 1.6 * clamp(Number.isFinite(py) ? py : 0, -1, 1),
})

// #endregion

export type KilnLink = { label: string; href: string }

export type KilnWordmarkFooterProps = {
  /** Used in the address column and the copyright. */
  brand: string
  /** What the giant mark spells. Lowercased; a–z, space, "-" and "." are drawn, anything else falls back to text. */
  wordmark: string
  /** One entry per line of the motto. Empty hides the top band (motto and back-to-top key). */
  motto?: string[]
  /** Address lines under the brand. */
  address: string[]
  navigationTitle?: string
  /** Columns of links. Empty hides the column. */
  navigation?: KilnLink[][]
  followTitle: string
  /** Columns of links. */
  socials: KilnLink[][]
  year: number
  /** Small print after the copyright. */
  registry?: React.ReactNode
  legal: KilnLink[]
  /** The disc and slab. */
  surface: string
  /** The field behind them. */
  deep: string
  /** Text, wordmark and the back-to-top key. */
  ink: string
  /** The arrow on the key. */
  paper: string
  /** Wordmark weight, 1 = reference. */
  weight?: number
  /** Size of the disc and slab, 1 = reference. Smaller reads better on a short panel. */
  tileScale?: number
  /**
   * Draw the wordmark as text in this CSS font-family instead of the geometric alphabet
   * (case kept). The letters still rise in and squash on click; under the pointer they
   * magnify instead of swelling, since a font has no stroke weight to animate.
   */
  wordmarkFont?: string
  /** All text in capitals, as in the original panel. */
  uppercase?: boolean
  /** Extra style for the column titles and the copyright (e.g. the site's display face). */
  headingStyle?: React.CSSProperties
}

const CSS =
  ".kwf{position:relative;isolation:isolate;overflow:hidden;container-type:inline-size;color:var(--kwf-ink);background:var(--kwf-deep);-webkit-font-smoothing:antialiased;text-transform:uppercase}" +
  ":where(.kwf) a{color:inherit;text-decoration:none}" +
  ":where(.kwf) button{font:inherit;color:inherit;background:none;border:0;padding:0;margin:0;cursor:pointer}" +
  ":where(.kwf) p,:where(.kwf) ul,:where(.kwf) h2,:where(.kwf) h3{margin:0;padding:0}" +
  ":where(.kwf) ul{list-style:none}" +
  ".kwf .sr-only{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}" +
  ".kwf-tile{position:absolute;inset:0;width:100%;height:100%;max-width:none;z-index:-1;display:block;pointer-events:none}" +
  ".kwf-in-wrap{position:relative;padding:1.5cqw 1.5cqw 1.5cqw}" +
  ".kwf-top{display:flex;justify-content:space-between;align-items:flex-start;gap:2cqw;min-height:26cqw}" +
  ".kwf-motto{font-size:clamp(20px,3.15cqw,52px);line-height:.94;font-weight:500;letter-spacing:-.012em}" +
  ".kwf-motto span{display:block}" +
  ".kwf-key{position:relative;flex:none;width:clamp(28px,3.15cqw,46px);height:clamp(54px,6.3cqw,92px);border-radius:clamp(6px,.8cqw,12px);background:var(--kwf-ink);color:var(--kwf-paper);overflow:hidden;display:grid;place-items:center;transition:transform .35s cubic-bezier(.2,.8,.2,1),border-radius .35s}" +
  ".kwf-key:hover{transform:translateY(-3px);border-radius:999px}" +
  ".kwf-key:active{transform:translateY(1px) scale(.96)}" +
  ".kwf-key svg{width:62%;height:auto;max-width:none;display:block;overflow:visible}" +
  ".kwf-key:hover svg,.kwf-key:focus-visible svg{animation:kwf-shoot .7s cubic-bezier(.6,0,.2,1)}" +
  ".kwf-key:focus-visible,.kwf-link:focus-visible{outline:2px solid var(--kwf-ink);outline-offset:3px}" +
  "@keyframes kwf-shoot{0%{transform:none}45%{transform:translateY(-160%)}46%{transform:translateY(160%)}100%{transform:none}}" +
  ".kwf-row{display:grid;grid-template-columns:29.8fr 50.2fr 18fr;gap:1.2cqw;font-size:clamp(10px,1.32cqw,16px);line-height:1.17;letter-spacing:-.005em}" +
  ".kwf-row-2{grid-template-columns:1fr auto}" +
  ".kwf-nocaps{text-transform:none}" +
  ".kwf-wordtext{line-height:1.1;white-space:nowrap;padding-top:.3em;margin-top:0;color:var(--kwf-ink)}" +
  ".kwf-wt,.kwf-wordtext .kwf-l,.kwf-lt{display:inline-block}" +
  ".kwf-lt{transform-origin:50% 100%;cursor:pointer}" +
  ".kwf-col h3{font-size:inherit;font-weight:700;margin-bottom:1.55em}" +
  ".kwf-cols{display:flex;gap:.55em 1.1em;flex-wrap:wrap}" +
  ".kwf-cols ul{display:flex;flex-direction:column}" +
  ".kwf-addr p{white-space:pre}" +
  ".kwf-link{position:relative;display:inline-block;padding:0 .14em;margin:0 -.14em;transition:color .25s}" +
  ".kwf-link::before{content:'';position:absolute;inset:-.06em 0;background:var(--kwf-ink);transform:scaleX(0);transform-origin:right;transition:transform .35s cubic-bezier(.7,0,.2,1);z-index:-1}" +
  ".kwf-link:hover,.kwf-link:focus-visible{color:var(--kwf-surface)}" +
  ".kwf-link:hover::before,.kwf-link:focus-visible::before{transform:scaleX(1);transform-origin:left}" +
  ".kwf-word{display:block;width:100%;height:auto;max-width:none;margin-top:2.3cqw;overflow:hidden;touch-action:pan-y;cursor:default}" +
  ".kwf-word text{text-transform:none}" +
  ".kwf-l{transform:translateY(118%);transform-box:fill-box;transition:transform 1s cubic-bezier(.16,.9,.18,1);transition-delay:var(--kwf-d,0ms)}" +
  ".kwf-l path{transform-box:fill-box;transform-origin:50% 100%;cursor:pointer}" +
  ".kwf-shown .kwf-l{transform:none}" +
  ".kwf-fade{opacity:0;transform:translateY(10px);transition:opacity .8s ease,transform .8s cubic-bezier(.2,.8,.2,1);transition-delay:var(--kwf-d,0ms)}" +
  ".kwf-shown .kwf-fade{opacity:1;transform:none}" +
  ".kwf-bar{margin-top:1.45cqw;border:1px solid var(--kwf-ink);border-radius:clamp(8px,1.05cqw,16px);padding:.35cqw 1.15cqw;display:flex;align-items:center;gap:1.5cqw;font-size:clamp(9px,1.02cqw,13px)}" +
  ".kwf-copy{font-size:clamp(14px,1.95cqw,28px);letter-spacing:-.01em;white-space:nowrap}" +
  ".kwf-reg{flex:1;min-width:0}" +
  ".kwf-legal{display:flex;gap:.35em;white-space:nowrap}" +
  "@container (max-width: 720px){" +
  ".kwf-in-wrap{padding:16px}" +
  ".kwf-top{min-height:44cqw}" +
  ".kwf-motto{font-size:clamp(22px,7cqw,40px)}" +
  ".kwf-row{grid-template-columns:1fr 1fr;gap:28px 16px;font-size:12px}" +
  ".kwf-nav{grid-column:1 / -1;grid-row:2}" +
  ".kwf-col h3{margin-bottom:.9em}" +
  ".kwf-cols{gap:.4em 1.4em}" +
  ".kwf-word{margin-top:28px}" +
  ".kwf-bar{flex-wrap:wrap;gap:4px 14px;padding:10px 14px;margin-top:12px;font-size:10px}" +
  ".kwf-reg{flex-basis:100%;order:3}" +
  ".kwf-legal{flex-wrap:wrap}" +
  "}" +
  "@media (prefers-reduced-motion: reduce){" +
  ".kwf-l,.kwf-fade{transform:none;opacity:1;transition:none}" +
  ".kwf-key,.kwf-link,.kwf-link::before{transition:none}" +
  ".kwf-key:hover svg,.kwf-key:focus-visible svg{animation:none}" +
  "}"

const BOOST = 0.42

function LinkItem({ link }: { link: KilnLink }) {
  const external = /^https?:/.test(link.href)
  return (
    <a className="kwf-link" href={link.href} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
      {link.label}
    </a>
  )
}

export default function KilnWordmarkFooter({
  brand,
  wordmark,
  motto = [],
  address,
  navigationTitle = "",
  navigation = [],
  followTitle,
  socials,
  year,
  registry,
  legal,
  surface,
  deep,
  ink,
  paper,
  weight = 1,
  tileScale = 1,
  wordmarkFont,
  uppercase = true,
  headingStyle,
}: KilnWordmarkFooterProps) {
  const word = wordmark.toLowerCase()
  const drawn = supported(word)
  const lay = React.useMemo(() => layoutWord(drawn ? word : ""), [word, drawn])
  const centres = React.useMemo(() => lay.letters.map((l) => l.x + l.w / 2), [lay])
  const base = clamp(Number.isFinite(weight) ? weight : 1, 0.5, 1.4)
  const textMode = !!wordmarkFont
  const chars = React.useMemo(() => [...wordmark], [wordmark])
  const count = textMode ? chars.length : lay.letters.length

  const rootRef = React.useRef<HTMLElement>(null)
  const pathRefs = React.useRef<(SVGPathElement | null)[]>([])
  const textRef = React.useRef<HTMLDivElement>(null)
  const letterRefs = React.useRef<(HTMLSpanElement | null)[]>([])
  const discRef = React.useRef<SVGEllipseElement>(null)
  const slabRef = React.useRef<SVGRectElement>(null)

  const [size, setSize] = React.useState({ w: 960, h: 540 })
  const [shown, setShown] = React.useState(false)
  const [tile, setTile] = React.useState(0)

  // Everything the animation loop owns. Written straight to the DOM, so a
  // pointer sweep never re-renders React.
  const anim = React.useRef({
    // the wordmark is a fixed prop: one weight per letter from the start
    k: Array.from({ length: count }, () => base),
    kT: Array.from({ length: count }, () => base),
    cur: scaleTile(TILES[0], tileScale),
    target: scaleTile(TILES[0], tileScale),
    lean: [0, 0] as [number, number],
    size: { w: 960, h: 540 },
    reduced: false,
    raf: 0,
    last: 0,
  })

  const paint = React.useCallback(() => {
    const a = anim.current
    const { w, h } = a.size
    const d = discRef.current
    if (d) {
      d.setAttribute("cx", n1(a.cur.cx * w))
      d.setAttribute("cy", n1(a.cur.cy * h))
      d.setAttribute("rx", n1(a.cur.r * w))
      d.setAttribute("ry", n1(a.cur.r * w))
    }
    const s = slabRef.current
    if (s) {
      s.setAttribute("x", n1(a.cur.sx * w))
      s.setAttribute("width", n1(Math.max(0, a.cur.sw * w)))
    }
    if (textMode) {
      // text: the "weight" is a magnification, lifted so the letter grows up from its baseline
      letterRefs.current.forEach((el, i) => {
        if (!el) return
        const k = a.k[i] / base
        el.style.transform = k === 1 ? "" : "translateY(" + n1((1 - k) * 40) + "%) scale(" + k.toFixed(3) + ")"
      })
      return
    }
    lay.letters.forEach((l, i) => {
      const p = pathRefs.current[i]
      if (p) p.setAttribute("d", glyphPath(l.ch, a.k[i], l.x))
    })
  }, [lay, textMode, base])

  const kick = React.useCallback(() => {
    const a = anim.current
    if (a.raf) return
    a.last = 0
    const step = (now: number) => {
      const dt = a.last ? (now - a.last) / 1000 : 1 / 60
      a.last = now
      const snap = a.reduced
      let busy = false
      const goal = leanTile(a.target, a.lean[0], a.lean[1])
      for (const key of ["cx", "cy", "r", "sx", "sw"] as const) {
        const next = snap ? goal[key] : approach(a.cur[key], goal[key], 0.075, dt)
        a.cur[key] = Math.abs(next - goal[key]) < 1e-4 ? goal[key] : next
        if (a.cur[key] !== goal[key]) busy = true
      }
      for (let i = 0; i < a.k.length; i++) {
        const next = snap ? a.kT[i] : approach(a.k[i], a.kT[i], 0.16, dt)
        a.k[i] = Math.abs(next - a.kT[i]) < 1e-3 ? a.kT[i] : next
        if (a.k[i] !== a.kT[i]) busy = true
      }
      paint()
      a.raf = busy ? requestAnimationFrame(step) : 0
    }
    a.raf = requestAnimationFrame(step)
  }, [paint])

  // Size, visibility, reduced motion.
  React.useEffect(() => {
    const el = rootRef.current
    if (!el) return
    const a = anim.current
    const mq = matchMedia("(prefers-reduced-motion: reduce)")
    a.reduced = mq.matches
    const onMq = () => {
      a.reduced = mq.matches
    }
    mq.addEventListener("change", onMq)

    const ro = new ResizeObserver(([entry]) => {
      const w = Math.max(1, Math.round(entry.contentRect.width))
      const h = Math.max(1, Math.round(entry.contentRect.height))
      a.size = { w, h }
      setSize((s) => (s.w === w && s.h === h ? s : { w, h }))
    })
    ro.observe(el)

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShown(true)
          io.disconnect()
        }
      },
      { threshold: 0.15 },
    )
    io.observe(el)

    return () => {
      ro.disconnect()
      io.disconnect()
      mq.removeEventListener("change", onMq)
      cancelAnimationFrame(a.raf)
      a.raf = 0
    }
  }, [])

  // A resize re-renders the tile at the new size; repaint the loop-owned attributes over it.
  React.useLayoutEffect(() => {
    paint()
  }, [size, paint])

  // Text wordmark: set the font size so the name spans the panel, again once the font has loaded.
  React.useLayoutEffect(() => {
    const box = textRef.current
    const inner = box?.firstElementChild as HTMLElement | null
    if (!box || !inner) return
    const fit = () => {
      box.style.fontSize = "100px"
      const w = inner.offsetWidth
      if (w) box.style.fontSize = (100 * box.clientWidth) / w + "px"
    }
    fit()
    document.fonts?.ready.then(fit)
  }, [size, wordmark, textMode])

  // The weight prop moves every resting letter.
  React.useEffect(() => {
    const a = anim.current
    a.kT = a.kT.map(() => base)
    kick()
  }, [base, kick])

  const turnTile = () => {
    const i = nextTile(tile)
    setTile(i)
    anim.current.target = scaleTile(TILES[i], tileScale)
    kick()
  }

  const onRootMove = (e: React.PointerEvent<HTMLElement>) => {
    if (e.pointerType === "touch") return
    const r = e.currentTarget.getBoundingClientRect()
    anim.current.lean = [((e.clientX - r.left) / r.width) * 2 - 1, ((e.clientY - r.top) / r.height) * 2 - 1]
    kick()
  }
  const onRootLeave = () => {
    anim.current.lean = [0, 0]
    kick()
  }
  const onRootClick = (e: React.MouseEvent<HTMLElement>) => {
    // Links, the key and the wordmark have their own clicks; text selection isn't a turn.
    if ((e.target as Element).closest("a,button,.kwf-word")) return
    if (window.getSelection?.()?.toString()) return
    turnTile()
  }

  const vbH = lay.bottom - lay.top
  const onWordMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!lay.width) return
    const r = e.currentTarget.getBoundingClientRect()
    const x = ((e.clientX - r.left) / r.width) * lay.width
    anim.current.kT = lensWeights(centres, x, base, BOOST, 150)
    kick()
  }
  const onTextMove = (e: React.PointerEvent<HTMLDivElement>) => {
    // centres from the wrappers, which the lens never scales
    const mids = letterRefs.current.map((el) => {
      const r = el?.parentElement?.getBoundingClientRect()
      return r ? r.left + r.width / 2 : 0
    })
    const em = parseFloat(e.currentTarget.style.fontSize) || 100
    anim.current.kT = lensWeights(mids, e.clientX, base, BOOST, em * 0.9)
    kick()
  }
  const onWordLeave = () => {
    anim.current.kT = anim.current.kT.map(() => base)
    kick()
  }
  const stamp = (i: number) => {
    const a = anim.current
    a.k[i] = Math.min(1.6, a.k[i] + 0.55)
    kick()
    const p = textMode ? letterRefs.current[i]?.parentElement : pathRefs.current[i]
    if (p && !a.reduced && typeof p.animate === "function") {
      p.animate(
        [
          { transform: "none" },
          { transform: "scale(1.05, 0.8)", offset: 0.25 },
          { transform: "scale(0.98, 1.05)", offset: 0.6 },
          { transform: "none" },
        ],
        { duration: 620, easing: "cubic-bezier(.3,.7,.3,1)" },
      )
    }
  }

  const backToTop = () => {
    document.scrollingElement?.scrollTo({ top: 0, behavior: anim.current.reduced ? "auto" : "smooth" })
  }

  // first render only: the loop repaints the tile straight into the DOM afterwards
  const t = scaleTile(TILES[0], tileScale)
  const vars = {
    "--kwf-surface": surface,
    "--kwf-deep": deep,
    "--kwf-ink": ink,
    "--kwf-paper": paper,
  } as React.CSSProperties

  const fade = (n: number) => ({ "--kwf-d": n * 45 + "ms" }) as React.CSSProperties

  return (
    <footer
      ref={rootRef}
      className={"kwf" + (shown ? " kwf-shown" : "") + (uppercase ? "" : " kwf-nocaps")}
      style={vars}
      onPointerMove={onRootMove}
      onPointerLeave={onRootLeave}
      onClick={onRootClick}
    >
      <style>{CSS}</style>

      <svg className="kwf-tile" viewBox={"0 0 " + size.w + " " + size.h} preserveAspectRatio="none" aria-hidden="true">
        <ellipse ref={discRef} cx={n1(t.cx * size.w)} cy={n1(t.cy * size.h)} rx={n1(t.r * size.w)} ry={n1(t.r * size.w)} fill={surface} />
        <rect ref={slabRef} x={n1(t.sx * size.w)} y={0} width={n1(Math.max(0, t.sw * size.w))} height={size.h} fill={surface} />
      </svg>

      <div className="kwf-in-wrap">
        {motto.length > 0 && (
        <div className="kwf-top">
          <h2 className="kwf-motto kwf-fade" style={fade(1)}>
            {motto.map((line, i) => (
              <span key={i}>{line}</span>
            ))}
          </h2>
          <button type="button" className="kwf-key kwf-fade" style={fade(2)} aria-label="Revenir en haut de la page" onClick={backToTop}>
            <svg viewBox="0 0 24 34" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <path d="M12 33V2M2 12 12 2l10 10" />
            </svg>
          </button>
        </div>
        )}

        <div className={"kwf-row" + (navigation.length ? "" : " kwf-row-2")}>
          <div className="kwf-col kwf-addr kwf-fade" style={fade(3)}>
            <h3 style={headingStyle}>{brand}</h3>
            {address.map((line, i) => (
              <p key={i}>{line}</p>
            ))}
          </div>
          {navigation.length > 0 && (
          <nav className="kwf-col kwf-nav kwf-fade" style={fade(4)} aria-label={navigationTitle}>
            <h3 style={headingStyle}>{navigationTitle}</h3>
            <div className="kwf-cols">
              {navigation.map((col, i) => (
                <ul key={i}>
                  {col.map((link) => (
                    <li key={link.label}>
                      <LinkItem link={link} />
                    </li>
                  ))}
                </ul>
              ))}
            </div>
          </nav>
          )}
          <div className="kwf-col kwf-fade" style={fade(5)}>
            <h3 style={headingStyle}>{followTitle}</h3>
            <div className="kwf-cols">
              {socials.map((col, i) => (
                <ul key={i}>
                  {col.map((link) => (
                    <li key={link.label}>
                      <LinkItem link={link} />
                    </li>
                  ))}
                </ul>
              ))}
            </div>
          </div>
        </div>

        <p className="sr-only">{textMode ? wordmark : word}</p>
        {textMode ? (
          <div
            ref={textRef}
            className="kwf-word kwf-wordtext"
            style={{ fontFamily: wordmarkFont }}
            aria-hidden="true"
            onPointerMove={onTextMove}
            onPointerLeave={onWordLeave}
          >
            <span className="kwf-wt">
              {chars.map((ch, i) => (
                <span key={i} className="kwf-l" style={{ "--kwf-d": 140 + i * 55 + "ms" } as React.CSSProperties}>
                  <span
                    ref={(el) => {
                      letterRefs.current[i] = el
                    }}
                    className="kwf-lt"
                    onClick={() => stamp(i)}
                  >
                    {ch === " " ? "\u00a0" : ch}
                  </span>
                </span>
              ))}
            </span>
          </div>
        ) : drawn ? (
          <svg
            className="kwf-word"
            viewBox={"0 " + lay.top + " " + lay.width + " " + vbH}
            aria-hidden="true"
            onPointerMove={onWordMove}
            onPointerLeave={onWordLeave}
          >
            {lay.letters.map((l, i) => (
              <g key={i + l.ch} className="kwf-l" style={{ "--kwf-d": 140 + i * 70 + "ms" } as React.CSSProperties}>
                <path
                  ref={(el) => {
                    pathRefs.current[i] = el
                  }}
                  d={glyphPath(l.ch, base, l.x)}
                  fill={ink}
                  onClick={() => stamp(i)}
                />
              </g>
            ))}
          </svg>
        ) : (
          <svg className="kwf-word" viewBox="0 0 1000 200" aria-hidden="true">
            <text x="0" y="168" textLength="1000" lengthAdjust="spacingAndGlyphs" fontSize="200" fontWeight="900" fill={ink}>
              {word}
            </text>
          </svg>
        )}

        <div className="kwf-bar kwf-fade" style={fade(6)}>
          <p className="kwf-copy" style={{ ...headingStyle, fontSize: undefined }}>
            ©{year} {brand}
          </p>
          <p className="kwf-reg">{registry}</p>
          <p className="kwf-legal">
            {legal.map((link, i) => (
              <React.Fragment key={link.label}>
                {i > 0 && <span aria-hidden="true">—</span>}
                <LinkItem link={link} />
              </React.Fragment>
            ))}
          </p>
        </div>
      </div>
    </footer>
  )
}
